import type {
  AlertFilters,
  AssetFilters,
  PlatformOrganizationFilters,
  RouteFilters,
  TrackerFilters,
  TripFilters,
} from '@core/types/api';

/**
 * Filter objects must be normalized before they enter a key.
 *
 * TanStack Query hashes keys structurally but key ORDER matters for the resulting hash,
 * and an explicit `undefined` is not the same as an absent key. Without this,
 * `{status: 'ONLINE', q: undefined}` and `{q: undefined, status: 'ONLINE'}` become two
 * cache entries for one logical query -- so an invalidation hits one and misses the other.
 */
export function normalizeFilters<T extends object>(filters: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .sort(([a], [b]) => a.localeCompare(b)),
  );
}

/**
 * ONE KEY, ONE SHAPE.
 *
 * <p>A key identifies the DATA, not the caller. Two queries may share a key only if they
 * resolve to the same type — otherwise whichever page loads last wins the cache entry and
 * the other reads a shape it was never written for.
 *
 * <p>That is not hypothetical. The fleet map and the trips picker both fetched "the
 * trackers, size 200" under `trackers.list({size: 200})`, but the map kept the whole
 * `PageResponse` and the picker kept a filtered array. Visiting the map and then the
 * trips screen threw `(allTrackers.data ?? []).map is not a function`; going the other
 * way was worse, because `data.content` on an array is simply `undefined` and the fleet
 * map quietly rendered an empty fleet. A derived projection gets its own key.
 *
 * <p>Every key lives here. An inline `queryKey: ['trackers', 'assignable']` at a call
 * site is how the collision above got written in the first place — you cannot see a
 * clash you have to go looking for in seven files.
 */
export const qk = {
  me: () => ['me'] as const,

  positions: {
    all: () => ['positions'] as const,
    /** Record<trackerId, PositionFix>. The single source of live state. */
    latest: () => ['positions', 'latest'] as const,
    history: (trackerId: string, from: string, to: string) =>
      ['positions', 'history', trackerId, from, to] as const,
    /**
     * A tracker's last satellite lock (`GnssLock | null`). One entry per tracker, fed by
     * the live stream as well as fetched: a key per report would start empty on every
     * cell fix and flash the pin to the cell tower until it loaded.
     */
    lastGnss: (trackerId: string) => ['positions', 'lastGnss', trackerId] as const,
  },

  trackers: {
    all: () => ['trackers'] as const,
    /** A `PageResponse<TrackerResponse>` — the envelope, not the rows. */
    list: (filters: TrackerFilters) => ['trackers', 'list', normalizeFilters(filters)] as const,
    detail: (id: string) => ['trackers', 'detail', id] as const,
    track: (id: string, from: string, to: string) => ['trackers', 'track', id, from, to] as const,
    /** `TrackerResponse[]`, unarchived, for the trips picker. A different shape from `list`. */
    pickable: () => ['trackers', 'pickable'] as const,
    /** `TrackerResponse[]`, unassigned, for the asset attach dialog. */
    assignable: () => ['trackers', 'assignable'] as const,
    /**
     * `TrackerConfigResponse` — device configuration. Under `trackers` so the blanket
     * tracker invalidation after any tracker write refreshes it too.
     */
    config: (id: string) => ['trackers', 'config', id] as const,
  },

  assets: {
    all: () => ['assets'] as const,
    list: (filters: AssetFilters) => ['assets', 'list', normalizeFilters(filters)] as const,
    detail: (id: string) => ['assets', 'detail', id] as const,
    /** `AssetResponse[]`, unarchived, for the route editor's vehicle picker. */
    pickable: () => ['assets', 'pickable'] as const,
  },

  trips: {
    all: () => ['trips'] as const,
    list: (filters: TripFilters) => ['trips', 'list', normalizeFilters(filters)] as const,
    detail: (id: string) => ['trips', 'detail', id] as const,
    /** The drawable track for one trip. Immutable once the trip has ended. */
    path: (id: string) => ['trips', 'path', id] as const,

    /**
     * The fleet map's trail for one selected vehicle. Separate from `list` and `path`
     * above, and not out of tidiness: the caching semantics genuinely differ. A replayed
     * trip is finished and cached forever; a trail is a trip still being driven, so its
     * key carries the trip's end time and it goes stale as the vehicle moves.
     */
    current: (trackerId: string, windowKey: string) =>
      ['trips', 'current', trackerId, windowKey] as const,
    /** Every `current` entry for one vehicle, whatever its window -- for invalidating. */
    currentFor: (trackerId: string) => ['trips', 'current', trackerId] as const,
    trail: (trackerId: string, tripId: string, endedAt: string) =>
      ['trips', 'trail', trackerId, tripId, endedAt] as const,
  },

  routes: {
    all: () => ['routes'] as const,
    /** A `PageResponse<RouteSummaryResponse>` -- summaries, with no geometry. */
    list: (filters: RouteFilters) => ['routes', 'list', normalizeFilters(filters)] as const,
    /** `RouteResponse` -- the detail, WITH the path. A different shape from a list row. */
    detail: (id: string) => ['routes', 'detail', id] as const,
  },

  routing: {
    all: () => ['routing'] as const,
    /**
     * A computed road route, keyed on a rounded coordinate signature.
     *
     * <p>Never on the waypoint objects: they carry ids and half-typed text that change
     * without changing the journey, and a dragged marker would otherwise produce a new
     * cache entry -- and a request -- per rendered frame.
     */
    directions: (signature: string) => ['routing', 'directions', signature] as const,
  },

  geocoding: {
    all: () => ['geocoding'] as const,
    /** `near` is pre-rounded by the caller, or panning the map keys per pixel. */
    search: (q: string, near: string | null) => ['geocoding', 'search', q, near] as const,
    reverse: (lat: number, lon: number) => ['geocoding', 'reverse', lat, lon] as const,
  },

  geofences: {
    all: () => ['geofences'] as const,
    list: () => ['geofences', 'list'] as const,
    detail: (id: string) => ['geofences', 'detail', id] as const,
    events: (filters: Record<string, unknown>) =>
      ['geofences', 'events', normalizeFilters(filters)] as const,
    /** The assets and trackers a fence can be scoped to. */
    scopeTargets: () => ['geofences', 'scope-targets'] as const,
  },

  alerts: {
    all: () => ['alerts'] as const,
    list: (filters: AlertFilters) => ['alerts', 'list', normalizeFilters(filters)] as const,
    openCount: () => ['alerts', 'open-count'] as const,
  },

  users: {
    all: () => ['users'] as const,
    list: () => ['users', 'list'] as const,
    invites: () => ['users', 'invites'] as const,
  },

  /**
   * Platform administration. Not organization-scoped: these answer the same way whichever
   * organization the tab has selected, which is why they live outside every tenant group.
   */
  platform: {
    all: () => ['platform'] as const,
    plans: () => ['platform', 'plans'] as const,
    /** Every organization-shaped entry, so one invalidation refreshes list and detail. */
    organizations: () => ['platform', 'organizations'] as const,
    /** A `PageResponse<PlatformOrganizationResponse>`. */
    organizationList: (filters: PlatformOrganizationFilters) =>
      ['platform', 'organizations', 'list', normalizeFilters(filters)] as const,
    /** One `PlatformOrganizationResponse`. */
    organization: (id: string) => ['platform', 'organizations', 'detail', id] as const,
    members: (id: string) => ['platform', 'organizations', 'members', id] as const,
    invites: (id: string) => ['platform', 'organizations', 'invites', id] as const,
    admins: () => ['platform', 'admins'] as const,
    /** People who have signed in, for the grant-admin search. */
    accounts: (q: string) => ['platform', 'accounts', q] as const,
  },

  organization: () => ['organization', 'current'] as const,
  /**
   * `TrackerConfigValues` — the organization's tracker defaults. Deliberately UNDER
   * `organization()`: the legacy `defaultReportIntervalSec` on the organization writes the
   * same value, so invalidating the organization must refresh this too.
   */
  organizationTrackerDefaults: () => ['organization', 'current', 'tracker-defaults'] as const,
  dashboard: () => ['dashboard', 'summary'] as const,
} as const;
